import { useCallback } from 'react';
import { useEditSceneModalPlaybackContext } from './EditSceneModalPlaybackContext';
import { useEditSceneModalStateContext } from './state';

/**
 * Hook that provides frame-accurate seeking with automatic flag management.
 * Delegates to SegmentedVideoPlayer.seekToFrameAccurate() which handles
 * all segmentation logic and event dispatching internally.
 */
export default function useFrameAccurateSeek() {
  const { dispatch } = useEditSceneModalStateContext();
  const { playerRef } = useEditSceneModalPlaybackContext();

  const seekToFrameAccurate = useCallback(
    async (targetLogicalSec: number): Promise<number> => {
      const player = playerRef.current;

      if (!player) {
        throw new Error('Player not available');
      }

      // Set flag - suppresses callbacks during seeking
      dispatch({ type: 'frame-seek-start' });

      try {
        const actualTime = await player.seekToFrameAccurate(targetLogicalSec);

        return actualTime;
      } finally {
        // Always clear flag, even if seeking fails
        dispatch({ type: 'frame-seek-end' });
      }
    },
    [dispatch, playerRef],
  );

  return seekToFrameAccurate;
}
